import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/common/Button';
import s from './TariffSlider.module.scss';
import { useNavigate } from 'react-router-dom';

type TariffFeatureTone = 'danger' | 'medium' | 'success';

type TariffFeature = {
  label: string;
  value: string;
  tone?: TariffFeatureTone;
};

type Tariff = {
  id: string;
  name: string;
  price: string;
  isCurrent?: boolean;
  features: TariffFeature[];
};

type TariffCardProps = {
  tariff: Tariff;
  isActive: boolean;
  currentPlanLabel: string;
  buyLabel: string;
  onBuy: () => void;
};

const TariffCard = ({
  tariff,
  isActive,
  currentPlanLabel,
  buyLabel,
  onBuy,
}: TariffCardProps) => {
  return (
    <article className={s.card} aria-hidden={!isActive}>
      <header className={s.card__header}>
        <div className={s['card__title-group']}>
          <h3 className={s.card__name}>{tariff.name}</h3>
          <p className={s.card__price}>{tariff.price}</p>
        </div>

        {tariff.isCurrent ? (
          <span className={s.card__status}>{currentPlanLabel}</span>
        ) : (
          <Button
            isAccent={true}
            isRounded={true}
            className={s.card__action}
            tabIndex={isActive ? undefined : -1}
            onClick={onBuy}
          >
            {buyLabel}
          </Button>
        )}
      </header>

      <dl className={s.card__features}>
        {tariff.features.map((feature) => (
          <div className={s.card__feature} key={feature.label}>
            <dt className={s['card__feature-label']}>{feature.label}</dt>
            <dd className={s['card__feature-value']} data-tone={feature.tone}>
              {feature.value}
            </dd>
          </div>
        ))}
      </dl>
    </article>
  );
};

export const TariffSlider = () => {
  const { t } = useTranslation();
  const [activeTariffIndex, setActiveTariffIndex] = useState(0);
  const navigate = useNavigate();
  const tariffs = useMemo<Tariff[]>(
    () => [
      {
        id: 'standard',
        name: 'Standard',
        price: '0 ₽',
        isCurrent: true,
        features: [
          {
            label: t('profile.pricesAndOperations.features.queuePriority'),
            value: t('profile.pricesAndOperations.values.low'),
            tone: 'medium',
          },
          {
            label: t('profile.pricesAndOperations.features.simpleExperts'),
            value: t('profile.pricesAndOperations.values.freeRequests'),
            tone: 'medium',
          },
          {
            label: t('profile.pricesAndOperations.features.thinkingPro'),
            value: t('profile.pricesAndOperations.values.unavailable'),
            tone: 'danger',
          },
          {
            label: t('profile.pricesAndOperations.features.multiAnswerDialog'),
            value: t('profile.pricesAndOperations.values.unavailable'),
            tone: 'danger',
          },
        ],
      },
      {
        id: 'plus',
        name: 'Plus',
        price: '490 ₽',
        features: [
          {
            label: t('profile.pricesAndOperations.features.queuePriority'),
            value: t('profile.pricesAndOperations.values.medium'),
            tone: 'medium',
          },
          {
            label: t('profile.pricesAndOperations.features.simpleExperts'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.thinkingPro'),
            value: t('profile.pricesAndOperations.values.limited'),
            tone: 'medium',
          },
          {
            label: t('profile.pricesAndOperations.features.multiAnswerDialog'),
            value: t('profile.pricesAndOperations.values.explorerDialog'),
            tone: 'medium',
          },
        ],
      },
      {
        id: 'pro',
        name: 'Pro',
        price: '1 490 ₽',
        features: [
          {
            label: t('profile.pricesAndOperations.features.queuePriority'),
            value: t('profile.pricesAndOperations.values.high'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.simpleExperts'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.thinkingPro'),
            value: t('profile.pricesAndOperations.values.limited'),
            tone: 'medium',
          },
          {
            label: t('profile.pricesAndOperations.features.multiAnswerDialog'),
            value: t('profile.pricesAndOperations.values.proDialog'),
            tone: 'medium',
          },
        ],
      },
      {
        id: 'advanced',
        name: 'Advanced',
        price: '9 900 ₽',
        features: [
          {
            label: t('profile.pricesAndOperations.features.queuePriority'),
            value: t('profile.pricesAndOperations.values.maximum'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.simpleExperts'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.thinkingPro'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.multiAnswerDialog'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
        ],
      },
      {
        id: 'scale',
        name: 'Scale',
        price: '9 900 ₽',
        features: [
          {
            label: t('profile.pricesAndOperations.features.queuePriority'),
            value: t('profile.pricesAndOperations.values.maximum'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.simpleExperts'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.thinkingPro'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.multiAnswerDialog'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
        ],
      },
      {
        id: 'ultimate',
        name: 'Ultimate',
        price: '14 900 ₽',
        features: [
          {
            label: t('profile.pricesAndOperations.features.queuePriority'),
            value: t('profile.pricesAndOperations.values.maximum'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.simpleExperts'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.thinkingPro'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
          {
            label: t('profile.pricesAndOperations.features.multiAnswerDialog'),
            value: t('profile.pricesAndOperations.values.unlimited'),
            tone: 'success',
          },
        ],
      },
    ],
    [t],
  );

  const activeTariff = tariffs[activeTariffIndex];
  const currentPlanLabel = t('profile.pricesAndOperations.currentPlan');
  const buyLabel = t('profile.pricesAndOperations.buy');

  return (
    <section
      className={s.tariffs}
      aria-label={t('profile.pricesAndOperations.tariffsLabel')}
    >
      <div className={s.tariffs__viewport}>
        <div
          className={s.tariffs__track}
          style={{ transform: `translateX(-${activeTariffIndex * 100}%)` }}
        >
          {tariffs.map((tariff) => (
            <TariffCard
              key={tariff.id}
              tariff={tariff}
              isActive={tariff.id === activeTariff.id}
              currentPlanLabel={currentPlanLabel}
              buyLabel={buyLabel}
              onBuy={() => navigate(`/payment?planId=${tariff.id}`)}
            />
          ))}
        </div>
      </div>

      <div className={s.tariffs__pagination}>
        {tariffs.map((tariff, index) => (
          <Button
            key={tariff.id}
            className={s.tariffs__dot}
            aria-label={t('profile.pricesAndOperations.selectTariff', {
              tariff: tariff.name,
            })}
            aria-current={index === activeTariffIndex}
            onClick={() => setActiveTariffIndex(index)}
          />
        ))}
      </div>
    </section>
  );
};
